import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IOverrides } from '@feathr/blackbox';
import { EPackages } from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  DatePicker,
  Icon,
  Input,
  Select,
  showError,
  Text,
  toast,
  Toggle,
  Value,
  Well,
} from '@feathr/components';
import { humanDate } from '@feathr/hooks';
import { isWretchError } from '@feathr/rachis';
import { useAccount, useRole } from '@feathr/report_components';

import { handleLogOut } from '../utils';

import * as styles from './PackageSwitcher.css';

interface IProps {
  isResetting: boolean;
}

interface IPackageOption {
  label: string;
  value: EPackages;
}

function PackageSwitcher({ isResetting }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { isGlobal } = useRole();
  const account = useAccount();

  // Internal state management
  const [newPackage, setNewPackage] = useState<EPackages | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);

  const overrides = useLocalObservable(() => ({
    numIdentifiedPersons: undefined as string | undefined,
    numMonthlyEmails: undefined as string | undefined,
    enableLimitsBilling: undefined as boolean | undefined,

    setNumIdentifiedPersons(value: string | undefined): void {
      overrides.numIdentifiedPersons = value;
    },

    setNumMonthlyEmails(value: string | undefined): void {
      overrides.numMonthlyEmails = value;
    },

    setEnableLimitsBilling(value: boolean | undefined): void {
      overrides.enableLimitsBilling = value;
    },

    clear(): void {
      overrides.numIdentifiedPersons = undefined;
      overrides.numMonthlyEmails = undefined;
      overrides.enableLimitsBilling = undefined;
    },

    get hasOverrides(): boolean {
      return (
        overrides.numIdentifiedPersons !== undefined ||
        overrides.numMonthlyEmails !== undefined ||
        overrides.enableLimitsBilling !== undefined
      );
    },

    get apiOverrides(): IOverrides {
      return {
        ...(overrides.numIdentifiedPersons !== undefined && {
          num_identified_persons: Number(overrides.numIdentifiedPersons),
        }),
        ...(overrides.numMonthlyEmails !== undefined && {
          num_monthly_emails: Number(overrides.numMonthlyEmails),
        }),
        ...(overrides.enableLimitsBilling !== undefined && {
          enable_limits_billing: overrides.enableLimitsBilling,
        }),
      };
    },
  }));
  const {
    period,
    num_identified_persons: numIdentifiedPersons,
    num_monthly_emails: numMonthlyEmails,
    enable_limits_billing: enableLimitsBilling,
  } = account.activePackage ?? {};
  const effectiveStartDate = period?.start;

  const humanEffectiveStartDate = useMemo(() => {
    return humanDate(effectiveStartDate ?? '');
  }, [effectiveStartDate]);

  const accountPackageOptions: IPackageOption[] = [
    { label: t('Feathr Platform - Lite'), value: EPackages.FeathrPlatformLite },
    { label: t('Feathr Platform - Base'), value: EPackages.FeathrPlatformBase },
    { label: t('Feathr Platform - Starter'), value: EPackages.FeathrPlatformStarter },
    { label: t('Feathr Platform - Essential'), value: EPackages.FeathrPlatformEssential },
    { label: t('Feathr Platform - Essential Pro'), value: EPackages.FeathrPlatformEssentialPro },
    { label: t('Feathr Platform - Advanced'), value: EPackages.FeathrPlatformAdvanced },
    { label: t('Feathr Platform - Advanced Pro'), value: EPackages.FeathrPlatformAdvancedPro },
    { label: t('Feathr Platform - Enterprise'), value: EPackages.FeathrPlatformEnterprise },
    { label: t('Feathr Platform - Enterprise Pro'), value: EPackages.FeathrPlatformEnterprisePro },
    { label: t('Finch'), value: EPackages.Finch },
    { label: t('Finch Legacy'), value: EPackages.FinchLegacy },
    { label: t('Falcon'), value: EPackages.Falcon },
    { label: t('Enterprise Falcon'), value: EPackages.EnterpriseFalcon },
    { label: t('Phoenix'), value: EPackages.Phoenix },
  ];

  function handleSelectNewPackage(newPackage?: IPackageOption): void {
    setNewPackage(newPackage?.value);
  }

  async function handleChangePackage(): Promise<void> {
    if (!newPackage) {
      toast(t('New package must be selected to change the package.'), {
        type: 'error',
      });
      return;
    }
    setIsProcessing(true);
    toast(t('Package change in progress. Once complete, you will be logged out.'), {
      type: 'info',
    });

    try {
      const response = await account.changeLicensePackage(
        newPackage,
        startDate,
        overrides.hasOverrides ? overrides.apiOverrides : undefined,
      );

      if (isWretchError(response)) {
        throw response.error;
      }

      handleLogOut(t);
    } catch (error) {
      showError(error, t);
      setIsProcessing(false);
    }
  }

  function handleClearSelectedPackage(): void {
    setNewPackage(undefined);
    setStartDate(undefined);
    overrides.clear();
  }

  function getButtonTooltip(): string | undefined {
    if (!isGlobal) {
      return t('Global role required');
    }
    if (!newPackage) {
      return t('New package not selected');
    }
    if (isResetting || isProcessing) {
      return t('Processing...');
    }
    return undefined;
  }

  const currentPackageOption = accountPackageOptions.find(
    (option) => option.value === account.activePackage?.name,
  );

  return (
    <Card disabled={isResetting || isProcessing}>
      <Card.Header
        description={t("Change the account's active package with optional overrides.")}
        title={t('Package Switcher')}
      />
      <Card.Content addVerticalGap={true}>
        <Value
          className={styles.currentPackage}
          prefix={t('Current package')}
          value={currentPackageOption?.label}
        />
        <Stack gap={8}>
          <Text.DefaultMedium>
            {t('Effective Start Date')}: {humanEffectiveStartDate}
          </Text.DefaultMedium>
          <div>
            <Text.DefaultMedium>{t('Overrides')}:</Text.DefaultMedium>
            <Well layout={'horizontal'}>
              <div>
                <strong>{t('Identified Persons')}:</strong>
                <br />
                {numIdentifiedPersons ?? t('Unlimited')}
              </div>
              <div>
                <strong>{t('Monthly Emails')}:</strong>
                <br />
                {numMonthlyEmails ?? t('Unlimited')}
              </div>
              <div>
                <strong>{t('Limits Billing')}:</strong>
                <br />
                {enableLimitsBilling ? t('Enabled') : t('Disabled')}
              </div>
            </Well>
          </div>
        </Stack>
      </Card.Content>
      <Card.Content addVerticalGap={true}>
        <Select<IPackageOption>
          getOptionLabel={(option): string => option.label}
          getOptionValue={(option): EPackages => option.value}
          isClearable={true}
          label={t('New package')}
          name={'new-package-select'}
          onClear={handleClearSelectedPackage}
          onSelectSingle={handleSelectNewPackage}
          options={accountPackageOptions.filter(
            (option) => option.value !== account.activePackage?.name,
          )}
          placeholder={t('Select package')}
        />
        {newPackage && (
          <>
            <DatePicker
              disabled={!newPackage}
              helpText={t('Effective date of the selected package. Can be retroactive.')}
              label={t('Start date')}
              onDateStrChange={setStartDate}
              placeholder={t('Select date')}
              showCalendarIcon={true}
              value={startDate}
            />

            <Input
              disabled={!newPackage || isResetting || isProcessing}
              helpText={t('Override the number of identified persons limit.')}
              label={t('Persons limit')}
              onChange={(value): void => overrides.setNumIdentifiedPersons(value)}
              placeholder={t('Use package default')}
              value={overrides.numIdentifiedPersons ?? ''}
            />

            <Input
              disabled={!newPackage || isResetting || isProcessing}
              helpText={t('Override the number of monthly emails limit.')}
              label={t('Email limit')}
              onChange={(value): void => overrides.setNumMonthlyEmails(value)}
              placeholder={t('Use package default')}
              value={overrides.numMonthlyEmails ?? ''}
            />

            <Toggle
              disabled={!newPackage || isResetting || isProcessing}
              helpText={t('Override the limits billing behavior.')}
              label={t('Enable limits billing')}
              layout={'well'}
              onChange={overrides.setEnableLimitsBilling}
              value={overrides.enableLimitsBilling}
            />
          </>
        )}
      </Card.Content>
      <Card.Actions>
        <Button
          disabled={!isGlobal || !newPackage || isResetting || isProcessing}
          onClick={handleChangePackage}
          prefix={<Icon icon={faArrowsRotate} />}
          tooltip={getButtonTooltip()}
          type={'primary'}
        >
          {t('Change package')}
        </Button>
      </Card.Actions>
    </Card>
  );
}

export default observer(PackageSwitcher);
